import { effectivePointSizeForRun, verticalAlignOffsetPx } from '@/lib/text/text-run-vertical-align'
import { buildCharSpans, resolveChunkStrut } from '../helpers/chunk-strut'
import { familyCss, textDecorationCss, variantStyle, variantWeight } from '../helpers/run-font-css'
import type {
  ShapeTextCharRunMap,
  ShapeTextChunk as ShapeTextChunkData,
  ShapeTextLayoutState,
} from '../types/shape-text-layout-state'

type FontDefaults = Pick<
  ShapeTextLayoutState,
  'fonts' | 'familyId' | 'cssFamily' | 'fontWeight' | 'fontStyle' | 'pointSize' | 'rowMetricPt'
>

/** One laid-out text chunk: a line-box block with its strut, holding one span per character. */
export function ShapeTextChunk({
  chunk,
  charRunMap,
  advances,
  dropCapCharIndices,
  fonts,
  familyId,
  cssFamily,
  fontWeight,
  fontStyle,
  pointSize,
  rowMetricPt,
}: FontDefaults & {
  chunk: ShapeTextChunkData
  charRunMap: ShapeTextCharRunMap
  advances: readonly number[]
  dropCapCharIndices: Set<number>
}) {
  const spans = buildCharSpans(chunk, charRunMap, advances)
  const { strutRun, strutSize } = resolveChunkStrut(spans, pointSize, rowMetricPt)
  const strutFamily = familyCss(strutRun?.fontFamilyId ?? familyId, fonts, cssFamily)

  return (
    <div
      className="absolute select-none whitespace-pre pointer-events-none"
      style={{
        left: chunk.x,
        top: chunk.yTop,
        // This chunk's own row's resolved thickness (`applyPerLineLeadingToFlow`), not the
        // frame-wide probe `flow.rowHeight` — different rows can now have different pitch.
        lineHeight: `${chunk.rowHeight}px`,
        // **Load-bearing, and the reason it is the row's TALLEST run.** A block with a
        // `line-height` also has a *strut* — an invisible inline box carrying the block's
        // own font — and CSS puts the line box's baseline at
        // `max over boxes of (lineHeight - inkᵢ)/2 + ascentᵢ`, the strut included. This div
        // used to set no `font-size` at all, so its strut inherited the app's ~16px body
        // font. That term grows with font size (`≈ 0.35 × size` for Arial), so for any text
        // smaller than the inherited size the STRUT won the max and placed the baseline —
        // dropping every glyph a couple of points below where `chunk.yTop` plus the run's
        // own half-leading says it sits. Measured on QA's star: a 9.2pt frame painted its
        // text 2.4pt lower than the model, which is what made the caret — positioned from
        // the run's metrics, correctly — read as "slightly above the line" (WMPCM-10123).
        // Setting the strut to the row's tallest run makes it exactly equal to the tallest
        // inline box, so it can never win the max on its own and the baseline is placed by
        // the real text, which is what the layout model assumes. The per-character spans
        // below still override both properties for themselves.
        fontSize: `${strutSize}px`,
        fontFamily: strutFamily,
        // `layoutShapeTextContent` advances the cursor by per-character measurements, so
        // every caret sits at a sum of single-glyph advances. Left to shape the chunk
        // normally the browser would apply pair kerning and ligatures and paint the text a
        // little narrower than that sum — a drift that accumulates left to right and reached
        // about a character by the end of a long row, putting each caret slightly right of
        // its glyph (WMPCM-8161). Painting unkerned is what keeps ink and carets identical.
        fontKerning: 'none',
        fontVariantLigatures: 'none',
      }}
    >
      {spans.map((span, spanIdx) => {
        const r = span.run
        // WMPCM-10141: measured (spAdvance, shape-text-measure.ts) and painted size must
        // agree, or a superscript/subscript character leaves a gap in its own pre-measured
        // box — see text-run-vertical-align.ts's header for why this is shared, not local.
        const spSize = effectivePointSizeForRun(r, pointSize)
        const spVerticalOffset = verticalAlignOffsetPx(r, r?.pointSize ?? pointSize)
        const spAdvance = span.advance
        // tracking and kerning are both stored as 1/1000 em (print industry
        // convention); convert to em for CSS — matches text-renderer.tsx
        const spTracking = ((r?.tracking ?? 0) + (r?.kerning ?? 0)) / 1000
        const isDropCapChar = dropCapCharIndices.has(span.charIndex)

        return (
          <span
            key={spanIdx}
            style={{
              // The box, not the glyph, carries the advance: `inline-block` at exactly the
              // width the caret math assumed pins every following glyph to its caret. The
              // only advance still clamped below its glyph's natural width is a break
              // space hanging at a segment edge, and clamping invisible whitespace paints
              // nothing to cut. The break-inside-word branch used to clamp a real letter
              // here and rely on the clip to slice the overhang; it no longer places a
              // glyph it cannot fit (WMPCM-10123 — see `layoutShapeTextContent`).
              display: 'inline-block',
              width: `${spAdvance}px`,
              fontFamily: familyCss(r?.fontFamilyId ?? familyId, fonts, cssFamily),
              // `px`: chunk positions come from a point-space layout against the shape's
              // own point geometry, so the glyphs must be sized on that same scale
              // (see `canvas-font-metrics.ts`).
              fontSize: `${spSize}px`,
              fontWeight: variantWeight(r?.fontId, fonts, fontWeight),
              fontStyle: variantStyle(r?.fontId, fonts, fontStyle),
              textDecoration: textDecorationCss(r),
              letterSpacing: spTracking !== 0 ? `${spTracking}em` : undefined,
              // The enlarged glyph is painted separately (see `DropCapGlyph`) — this span
              // stays in the flow at its normal small size so every following glyph's caret
              // position is unaffected, it just isn't drawn twice.
              visibility: isDropCapChar ? 'hidden' : undefined,
              // WMPCM-10141 — baseline shift for superscript/subscript, `0` (a no-op) for
              // every other run. `relative` so the box's own width (the caret math) is
              // unaffected by the shift.
              position: spVerticalOffset !== 0 ? 'relative' : undefined,
              top: spVerticalOffset !== 0 ? `${spVerticalOffset}px` : undefined,
            }}
          >
            {span.ch}
          </span>
        )
      })}
    </div>
  )
}
